/* eslint-disable react/no-unescaped-entities --
 * v77.99.140 (iter 124.exports-inbox) — Unified "Exports" page.
 *
 * Central inbox for every async document the platform generates:
 *   - Expenses PDFs (single-month, snapshot, custom range)
 *   - Court Bundles (single + batch)
 *   - Contact Visit reports (future)
 *   - AI reports (future)
 *
 * Long-term product intent: this page is the seed for a full
 * "Document Centre" — same page shape will later expand to include
 * signed court orders, uploaded evidence and correspondence exports.
 *
 * The React 19 `set-state-in-effect` rule mis-flags the standard
 * effect-driven state hydration we use across the app after a fetch;
 * disabled here to match the convention in CourtBundleHistory.jsx.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API, api, getToken } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Clock, Download, FileWarning, Inbox as InboxIcon, Loader2, RefreshCw, ShieldCheck,
  FileText, FileDown, Sparkles, HandHelping, RotateCcw, Timer, Gavel,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Visual language shared with CourtBundleHistory — same status pill
 * palette, same surface cards, same sentence-case status labels.
 */
const STATUS_PILL = {
  ready:   { label: "Ready",      className: "bg-[#D6F0EF] text-[#0F5658] border-[#A7D8DA]" },
  running: { label: "Processing", className: "bg-[#FFF8E8] text-[#5C4516] border-[#E6CFA2]" },
  queued:  { label: "Queued",     className: "bg-[#F2F5F6] text-[#3F595B] border-[#D9E1E3]" },
  failed:  { label: "Failed",     className: "bg-[#FBEFEE] text-[#A93A2F] border-[#E6BAB6]" },
  expired: { label: "Expired",    className: "bg-[#F2F5F6] text-[#6F828A] border-[#D9E1E3]" },
};

/**
 * Type-specific presentation (icon, human name, optional cross-link
 * back to the source page that generated the export). Add new export
 * types here as they're introduced — everything else on the page is
 * fully data-driven.
 */
const TYPE_META = {
  expenses_pdf: {
    icon: FileText,
    label: "Expenses PDF",
    accent: "text-[#116B6E]",
    source: { to: "/app/expenses", label: "Open Expenses" },
  },
  court_bundle: {
    icon: FileDown,
    label: "Court Bundle",
    accent: "text-[#116B6E]",
    source: { to: "/app/court-bundle", label: "Open Court Bundle" },
  },
  contact_visit_report: {
    icon: HandHelping,
    label: "Contact Visit report",
    accent: "text-[#116B6E]",
    source: { to: "/app/contact-visits", label: "Open Contact Visits" },
  },
  ai_report: {
    icon: Sparkles,
    label: "AI report",
    accent: "text-[#116B6E]",
    source: null,
  },
  court_order_certified: {
    icon: Gavel,
    label: "Court Order (certified copy)",
    accent: "text-[#116B6E]",
    source: { to: "/app/court-orders", label: "Open Court Orders" },
  },
};

/**
 * Fallback when an unrecognised type comes back from the API — we
 * still render the row but with a neutral document icon and a title-
 * cased slug so the user isn't left staring at "unknown".
 */
function _typeMetaFor(type) {
  return TYPE_META[type] || {
    icon: FileText,
    label: (type || "Document")
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase()),
    accent: "text-[#116B6E]",
    source: null,
  };
}

function _formatBytes(n) {
  if (!n || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function _formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * Relative-time formatter — "5 min ago" / "in 3 days".
 * Purpose-built for the two most useful spots on this page:
 *   1. Created time on every row.
 *   2. Expiry countdown on ready rows ("Available for another 6 days").
 */
function _formatRelative(iso, { future = false } = {}) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const deltaSec = Math.round((d.getTime() - Date.now()) / 1000) * (future ? 1 : -1);
  const abs = Math.abs(deltaSec);
  if (abs < 60) return future ? "in a moment" : "just now";
  if (abs < 3600) {
    const m = Math.round(abs / 60);
    return future ? `in ${m} min` : `${m} min ago`;
  }
  if (abs < 86400) {
    const h = Math.round(abs / 3600);
    return future ? `in ${h} h` : `${h} h ago`;
  }
  const days = Math.round(abs / 86400);
  if (days < 30) return future ? `in ${days} day${days === 1 ? "" : "s"}` : `${days} day${days === 1 ? "" : "s"} ago`;
  return _formatDate(iso);
}

/**
 * True when a `ready` job has already passed its expires_at cutoff.
 * The server's opportunistic prune will remove it on the next
 * request, but the row can hang around briefly until then — flag it
 * as expired so we don't show a broken download button.
 */
function _isExpired(job) {
  if (job.status !== "ready") return false;
  if (!job.expires_at) return false;
  try {
    return new Date(job.expires_at).getTime() < Date.now();
  } catch {
    return false;
  }
}

export default function ExportsInbox() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await api.get("/exports/jobs/list?days=30&limit=100");
      setJobs(r.data?.jobs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load exports.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while any in-flight jobs are visible — mirrors how
  // the Court Bundle page polls its own build. 3s cadence is snappy
  // enough for the "Processing" -> "Ready" transition to feel live
  // without hammering the backend.
  const hasInFlight = useMemo(
    () => jobs.some((j) => j.status === "queued" || j.status === "running"),
    [jobs],
  );
  const pollRef = useRef(null);
  useEffect(() => {
    if (!hasInFlight) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    pollRef.current = setInterval(() => { load(true); }, 3000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [hasInFlight, load]);

  const downloadJob = async (job) => {
    if (job.status !== "ready") {
      toast.error("This export isn't ready yet.");
      return;
    }
    setDownloadingId(job.id);
    try {
      const token = getToken();
      const resp = await fetch(`${API}/exports/jobs/${job.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || `Download failed (${resp.status}).`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = job.filename || `export-${job.id}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started.");
    } catch (e) {
      toast.error(e.message || "Download failed.");
    } finally {
      setDownloadingId(null);
    }
  };

  const retryJob = async (job) => {
    if (job.status !== "failed") {
      toast.error("Only failed exports can be retried.");
      return;
    }
    setRetryingId(job.id);
    try {
      const r = await api.post(`/exports/jobs/${job.id}/retry`);
      const newId = r.data?.job_id;
      if (r.data?.deduped) {
        toast.success("Reusing an existing in-flight export for the same report.");
      } else {
        toast.success("Export re-queued — we'll update this list as it progresses.");
      }
      // Optimistic refresh so the new row appears immediately.
      await load(true);
      // Highlight the new row briefly (visual only — no persistent state).
      if (newId) {
        try {
          const el = document.querySelector(`[data-testid="exports-row-${newId}"]`);
          if (el && typeof el.scrollIntoView === "function") {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        } catch { /* noop */ }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.cta_url) {
        toast.error(detail.message || "Please retry from the source page.", {
          action: {
            label: "Open",
            onClick: () => { window.location.href = detail.cta_url; },
          },
          duration: 8000,
        });
      } else {
        toast.error(
          (typeof detail === "string" ? detail : detail?.message) || "Retry failed. Please try again.",
        );
      }
    } finally {
      setRetryingId(null);
    }
  };

  const readyCount = jobs.filter((j) => j.status === "ready" && !_isExpired(j)).length;
  const inFlightCount = jobs.filter((j) => j.status === "queued" || j.status === "running").length;
  const failedCount = jobs.filter((j) => j.status === "failed").length;

  return (
    <div className="space-y-8" data-testid="exports-inbox-page">
      <div>
        <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Documents</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1 inline-flex items-center gap-3">
          <InboxIcon className="w-7 h-7 text-[#116B6E]" strokeWidth={1.5} />
          Exports
        </h1>
        <p className="text-[#6F828A] mt-2 text-base max-w-2xl leading-relaxed">
          Every report and bundle you've asked us to generate over the last 30 days. Ready files stay available for 7 days after completion so you can come back to them without rebuilding.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4]"
            data-testid="exports-refresh-button"
          >
            {refreshing
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.8} />
              : <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.6} />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary strip — one glance tells you if anything needs your
          attention (failed jobs) or if something's still being built. */}
      {!loading && jobs.length > 0 && (
        <div className="grid grid-cols-3 gap-3 max-w-2xl" data-testid="exports-summary">
          <div className="border border-[#D9E1E3] rounded-md bg-white px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.08em] text-[#6F828A] font-semibold">Ready</div>
            <div className="font-heading text-2xl text-[#0F5658] mt-1 font-mono-data" data-testid="exports-count-ready">{readyCount}</div>
          </div>
          <div className="border border-[#D9E1E3] rounded-md bg-white px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.08em] text-[#6F828A] font-semibold">Processing</div>
            <div className="font-heading text-2xl text-[#5C4516] mt-1 font-mono-data" data-testid="exports-count-processing">{inFlightCount}</div>
          </div>
          <div className="border border-[#D9E1E3] rounded-md bg-white px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.08em] text-[#6F828A] font-semibold">Failed</div>
            <div className="font-heading text-2xl text-[#A93A2F] mt-1 font-mono-data" data-testid="exports-count-failed">{failedCount}</div>
          </div>
        </div>
      )}

      {loading ? (
        <Card className="border-[#D9E1E3] rounded-md shadow-none" data-testid="exports-loading-card">
          <CardContent className="p-8 text-center text-sm text-[#6F828A]">
            <Loader2 className="w-5 h-5 inline-block animate-spin mr-2 text-[#116B6E]" strokeWidth={1.8} />
            Loading your exports…
          </CardContent>
        </Card>
      ) : jobs.length === 0 ? (
        <Card className="border-[#D9E1E3] rounded-md shadow-none" data-testid="exports-empty-card">
          <CardContent className="p-8 text-center">
            <ShieldCheck className="w-9 h-9 mx-auto text-[#116B6E] mb-3" strokeWidth={1.5} />
            <div className="font-heading text-lg text-[#2E3A3F]">No exports yet</div>
            <p className="text-sm text-[#6F828A] mt-2 max-w-md mx-auto">
              When you download a large report or ask us to email it, it'll appear here so you can re-download it anytime in the next 7 days.
            </p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              <Button type="button" variant="outline" asChild className="border-[#A6D7D9] text-[#116B6E] hover:bg-[#D6F0EF]">
                <Link to="/app/expenses">Go to Expenses</Link>
              </Button>
              <Button type="button" asChild className="bg-[#116B6E] hover:bg-[#0F5658] text-white">
                <Link to="/app/court-bundle">Generate a Court Bundle</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#D9E1E3] rounded-md shadow-none" data-testid="exports-list-card">
          <CardHeader className="border-b border-[#D9E1E3] bg-[#FAFCFC] py-3">
            <CardTitle className="font-heading text-sm inline-flex items-center gap-2 text-[#2E3A3F]">
              <Clock className="w-4 h-4 text-[#116B6E]" strokeWidth={1.6} />
              Recent exports · last 30 days
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-[#EEF3F4]" data-testid="exports-list">
              {jobs.map((job) => {
                const meta = _typeMetaFor(job.type);
                const RowIcon = meta.icon;
                const expired = _isExpired(job);
                const effectiveStatus = expired ? "expired" : job.status;
                const pill = STATUS_PILL[effectiveStatus] || STATUS_PILL.queued;
                const isDownloading = downloadingId === job.id;
                const isRetrying = retryingId === job.id;
                return (
                  <li
                    key={job.id}
                    className="px-4 py-4 flex flex-col sm:flex-row sm:items-start gap-3"
                    data-testid={`exports-row-${job.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5">
                          <RowIcon className={`w-4 h-4 ${meta.accent}`} strokeWidth={1.6} />
                          <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-[#6F828A]">
                            {meta.label}
                          </span>
                        </span>
                        <span
                          className={`inline-flex items-center text-[10px] uppercase tracking-[0.08em] font-semibold rounded-sm px-1.5 py-0.5 border ${pill.className}`}
                          data-testid={`exports-status-${job.id}`}
                        >
                          {pill.label}
                        </span>
                        {job.status === "running" && (job.progress_pct != null) && (
                          <span className="text-[11px] text-[#6F828A] font-mono-data">
                            {job.progress_pct}%
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-medium text-[#2E3A3F] mt-1.5" data-testid={`exports-label-${job.id}`}>
                        {job.label || job.filename || meta.label}
                      </div>
                      <div className="text-[11px] text-[#6F828A] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span title={_formatDate(job.created_at)}>Requested {_formatRelative(job.created_at)}</span>
                        {job.size_bytes ? <span>{_formatBytes(job.size_bytes)}</span> : null}
                        {job.status === "ready" && !expired && job.expires_at && (
                          <span className="inline-flex items-center gap-1" title={`Expires ${_formatDate(job.expires_at)}`}>
                            <Timer className="w-3 h-3" strokeWidth={1.6} />
                            Available for another {_formatRelative(job.expires_at, { future: true }).replace(/^in\s+/, "")}
                          </span>
                        )}
                        {expired && (
                          <span className="text-[#6F828A]">Expired {_formatRelative(job.expires_at)}</span>
                        )}
                        {job.status === "failed" && (
                          <span className="text-[#A93A2F] inline-flex items-center gap-1">
                            <FileWarning className="w-3 h-3" strokeWidth={1.6} />
                            {(job.error || "Generation failed").slice(0, 160)}
                          </span>
                        )}
                        {job.status === "running" && job.progress_stage && (
                          <span className="text-[#5C4516] font-mono-data">{job.progress_stage.replace(/_/g, " ")}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:flex-shrink-0 sm:pt-0.5">
                      {job.status === "ready" && !expired ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => downloadJob(job)}
                          disabled={isDownloading}
                          className="bg-[#116B6E] hover:bg-[#0F5658] text-white"
                          data-testid={`exports-download-${job.id}`}
                        >
                          {isDownloading ? (
                            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={1.8} /> Downloading…</>
                          ) : (
                            <><Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.6} /> Download</>
                          )}
                        </Button>
                      ) : job.status === "failed" ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => retryJob(job)}
                            disabled={isRetrying}
                            className="border-[#A6D7D9] text-[#116B6E] hover:bg-[#D6F0EF]"
                            data-testid={`exports-retry-${job.id}`}
                          >
                            {isRetrying ? (
                              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={1.8} /> Retrying…</>
                            ) : (
                              <><RotateCcw className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.6} /> Retry</>
                            )}
                          </Button>
                          {meta.source && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              asChild
                              className="text-[#6F828A] hover:text-[#2E3A3F]"
                              data-testid={`exports-source-${job.id}`}
                            >
                              <Link to={meta.source.to}>{meta.source.label}</Link>
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled
                          className="text-[#6F828A]"
                          data-testid={`exports-disabled-${job.id}`}
                        >
                          {expired ? "Expired" : job.status === "running" ? "Processing…" : "Queued"}
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="text-xs text-[#6F828A] flex items-start gap-2 max-w-2xl leading-relaxed">
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#116B6E]" strokeWidth={1.6} />
        <span>
          Downloads require you to be signed in. Generated PDFs are encrypted at rest and never shared outside your household — only you can re-download them from this page. Files are kept for 7 days after completion.
        </span>
      </div>
    </div>
  );
}
